import { useEffect, useState, useRef } from 'react';
import {
  IPlacemarkOptions,
  IPolygonOptions,
  Map,
  Placemark,
  Polygon,
} from 'yandex-maps';
import { SleeveManager } from '@lib/sleeve-manager';
import { ButtonWithIcon } from '@ui/button';
import { getGradientCoords } from './utils/getGradientCoords.util';
import {
  checkWarehouseIntersection,
  checkWarehouseIntersectionByPoint,
} from './utils/checkWarehouseIntersection.util';
import {
  CreateWarehouseInputImageBase64,
  CreateWarehouseOutputImageBase64,
} from './images/input-output.images';
import { CrossIcon } from '@@/common/icons/cross.icon';

import styles from './styles.module.css';

const DEFAULT_VIEWER_SETTINGS = {
  center: [45.04484, 38.97603],
  zoom: 15,
  // controls: [],
  controls: ['zoomControl', 'geolocationControl', 'typeSelector', 'fullscreenControl'],
};

export type Coordinate = number[]; // latitude, longitude;
export type CoordinateObject = { latitude: number; longitude: number };

export type DispositionSchema = [number, number][][];

export type modes =
  | 'none'
  | 'setPolygon'
  | 'setMarker'
  | 'setAngle'
  | 'setTopAngle'
  | 'setBottomAngle'
  | 'setInput'
  | 'setOutput'
  | 'setSleeves';

export type CreateMapProps = {
  center: Coordinate;
  zoom: number;
  type: 'fixed' | 'default';
  controls: string[];
  mode: modes;
  sleeveLength: number;
  restrictedAreas: number[][][];
  disposition: DispositionSchema;
  onSquareChange?: (square: number) => void | Promise<void>;
  onPolygonEnd?: Function;
  onCloseSetAngle?: Function;
  onUpdateTopAngle?: Function;
  onUpdateBottomAngle?: Function;
  onUpdateInput?: (coords: CoordinateObject) => void | Promise<void>;
  onUpdateOutput?: (coords: CoordinateObject) => void | Promise<void>;
  onSendCoordinates?: (coordinates: number[][]) => void | Promise<void>;
  onSetMarker?: (coordinate: Coordinate) => void | Promise<void>;
  onBugsCount?: Function;
  onBugsVolume?: (volume: number) => void;
  onMapReady?: Function;
  onIntersectionCheck?: Function;
};

let map: Map;
let polygon: ymaps.Polygon;
let topCircle: ymaps.Placemark;
let bottomCircle: ymaps.Placemark;
let directionArrow: ymaps.GeoObject;
let inputMark: ymaps.Placemark | null;
let outputMark: ymaps.Placemark | null;
let marker: ymaps.Placemark;
let markers: ymaps.Placemark[] = [];
let slvManager: SleeveManager<number[][][]>;
let restrictedPolygons: ymaps.Polygon[] = [];

export const CreateMap = ({
  center,
  zoom,
  controls,
  mode,
  restrictedAreas,
  disposition,
  sleeveLength,
  onSquareChange,
  onPolygonEnd,
  onCloseSetAngle,
  onUpdateTopAngle,
  onUpdateBottomAngle,
  onUpdateInput,
  onUpdateOutput,
  onSendCoordinates,
  onSetMarker,
  onBugsCount,
  onBugsVolume,
  onMapReady,
  onIntersectionCheck,
}: CreateMapProps) => {
  const [mapReady, setMapReady] = useState(false);
  const containerDomRef = useRef<HTMLDivElement>(null);
  const [localMode, setLocalMode] = useState(mode);
  const [markerPoint, setMarkerPoint] = useState();
  const [topAngle, setTopAngle] = useState();
  const [bottomAngle, setBottomAngle] = useState();

  const onMapClick = (event: ymaps.IEvent<MouseEvent>) => {
    if (localMode === 'setMarker') {
      const coords = event.get('coords');
      if (marker) map.geoObjects.remove(marker);

      marker = new ymaps.Placemark(
        coords,
        {},
        {
          iconLayout: 'default#image',
          iconImageHref:
            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDciIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0NyA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjMuNSIgY3k9IjIzLjc2MjIiIHI9IjIxLjk2NDMiIHRyYW5zZm9ybT0icm90YXRlKC0xODAgMjMuNSAyMy43NjIyKSIgZmlsbD0id2hpdGUiIHN0cm9rZT0iIzQwOUJGRiIgc3Ryb2tlLXdpZHRoPSIyLjkyODU3Ii8+CjxwYXRoIGQ9Ik0yMy40OTgzIDE3LjkwNjNDMjYuNzMzMSAxNy45MDYzIDI5LjM1NTUgMjAuNTI4NiAyOS4zNTU1IDIzLjc2MzRDMjkuMzU1NSAyNi45OTgyIDI2LjczMzEgMjkuNjIwNSAyMy40OTgzIDI5LjYyMDVDMjAuMjYzNSAyOS42MjA1IDE3LjY0MTIgMjYuOTk4MiAxNy42NDEyIDIzLjc2MzRDMTcuNjQxMiAyMC41Mjg2IDIwLjI2MzUgMTcuOTA2MyAyMy40OTgzIDE3LjkwNjNaIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSIjNDA5QkZGIiBzdHJva2Utd2lkdGg9IjIuOTI4NTciLz4KPHBhdGggZD0iTTIzLjUgMTcuOTA1MVY4LjM4NzIxIiBzdHJva2U9IiM0MDlCRkYiIHN0cm9rZS13aWR0aD0iMi45Mjg1NyIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+CjxwYXRoIGQ9Ik0yMy41IDM5LjEzNTVWMjkuNjE3NyIgc3Ryb2tlPSIjNDA5QkZGIiBzdHJva2Utd2lkdGg9IjIuOTI4NTciIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8cGF0aCBkPSJNMjkuMzU2NyAyMy43NjIyTDM1LjIxMzkgMjMuNzYyMiIgc3Ryb2tlPSIjNDA5QkZGIiBzdHJva2Utd2lkdGg9IjIuOTI4NTciIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTEuNzg0NSAyMy43NjIyTDE3LjY0MTYgMjMuNzYyMiIgc3Ryb2tlPSIjNDA5QkZGIiBzdHJva2Utd2lkdGg9IjIuOTI4NTciIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L3N2Zz4K',
          iconImageSize: [42, 42],
          iconImageOffset: [-21, -21],
        },
      );

      map.geoObjects.add(marker);
      if (onSetMarker) onSetMarker(coords);

      if (onIntersectionCheck)
        onIntersectionCheck(checkWarehouseIntersectionByPoint(coords, restrictedAreas));
    }

    // if (mode === 'setTopAngle') {
    //   if (onSendCoordinates) {
    //     const bounds: number[][] = polygon.geometry?.getBounds() || [[], []];
    //     const delta = [
    //       Math.abs(bounds[0][0] - bounds[1][0]) / 2,
    //       Math.abs(bounds[0][1] - bounds[1][1]) / 2,
    //     ];
    //     const marker = [
    //       bounds[0][0] > bounds[1][0] ? bounds[1][0] + delta[0] : bounds[0][0] + delta[0],
    //       bounds[0][1] > bounds[1][1] ? bounds[1][1] + delta[1] : bounds[0][1] + delta[1],
    //     ];
    //
    //     setMarkerPoint(marker);
    //
    //     // TODO: fix
    //     onSendCoordinates({
    //       position: polygon.geometry?.getCoordinates()[0].slice(0, -1),
    //       marker,
    //     });
    //
    //     const coords = event.get('coords');
    //     if (!polygon.geometry?.contains(coords)) return;
    //     if (topCircle) map.geoObjects.remove(topCircle);
    //
    //     topCircle = new ymaps.Placemark(
    //       coords,
    //       {},
    //       {
    //         iconLayout: ymaps.templateLayoutFactory.createClass(
    //           `<div style="transform:rotate({{options.rotate}}deg);">
    //         {% include "default#image" %}
    //       </div>`,
    //         ),
    //         iconImageHref:
    //           'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iOS4yODU2NCIgY3k9IjEwIiByPSI4LjU3MTQzIiB0cmFuc2Zvcm09InJvdGF0ZSgxODAgOS4yODU2NCAxMCkiIGZpbGw9IndoaXRlIiBzdHJva2U9IiM2NkIyOEEiIHN0cm9rZS13aWR0aD0iMS4xNDI4NiIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTkuMDAwMDcgMTQuODU3M0M4Ljc2MzM4IDE0Ljg1NzMgOC41NzE1IDE0LjY2NTQgOC41NzE1IDE0LjQyODdWNi44OTE5NUw1LjQ0NTk3IDEwLjAxNzVDNS4yNzg2IDEwLjE4NDggNS4wMDcyNSAxMC4xODQ4IDQuODM5ODggMTAuMDE3NUM0LjY3MjUxIDkuODUwMSA0LjY3MjUxIDkuNTc4NzUgNC44Mzk4OCA5LjQxMTM4TDguNjk3MDIgNS41NTQyNEM4Ljg2NDM5IDUuMzg2ODcgOS4xMzU3NSA1LjM4Njg3IDkuMzAzMTIgNS41NTQyNEwxMy4xNjAzIDkuNDExMzhDMTMuMzI3NiA5LjU3ODc1IDEzLjMyNzYgOS44NTAxIDEzLjE2MDMgMTAuMDE3NUMxMi45OTI5IDEwLjE4NDggMTIuNzIxNSAxMC4xODQ4IDEyLjU1NDIgMTAuMDE3NUw5LjQyODY0IDYuODkxOTVWMTQuNDI4N0M5LjQyODY0IDE0LjY2NTQgOS4yMzY3NiAxNC44NTczIDkuMDAwMDcgMTQuODU3M1oiIGZpbGw9IiM2NkIyOEEiLz4KPC9zdmc+Cg==',
    //         iconImageSize: [28, 28],
    //         iconImageOffset: [-14, -14],
    //       },
    //     );
    //
    //     setTopAngle(coords);
    //
    //     map.geoObjects.add(topCircle);
    //     onUpdateTopAngle?.({
    //       latitude: coords[0],
    //       longitude: coords[1],
    //     });
    //
    //     if (bottomAngle) {
    //       setGradient(polygon, coords, bottomAngle);
    //       setDirectionArrow(coords, bottomAngle);
    //
    //       const angle =
    //         (Math.atan2(bottomAngle[1] - coords[1], bottomAngle[0] - coords[0]) * 180) / Math.PI;
    //
    //       topCircle.options.set('iconRotate', angle);
    //       if (bottomCircle) bottomCircle.options.set('iconRotate', angle + 180);
    //     }
    //   }
    //
    //   return;
    // }

    // if (mode === 'setBottomAngle') {
    //   const coords = event.get('coords');
    //
    //   if (!polygon.geometry?.contains(coords)) return;
    //   if (bottomCircle) map.geoObjects.remove(bottomCircle);
    //
    //   bottomCircle = new ymaps.Placemark(
    //     coords,
    //     {},
    //     {
    //       iconLayout: ymaps.templateLayoutFactory.createClass(
    //         `<div style="transform:rotate({{options.rotate}}deg);">
    //         {% include "default#image" %}
    //       </div>`,
    //       ),
    //       iconImageHref:
    //         'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTkiIHZpZXdCb3g9IjAgMCAxOSAxOSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iOS43MTQzNiIgY3k9IjkuNSIgcj0iOC41NzE0MyIgZmlsbD0id2hpdGUiIHN0cm9rZT0iI0YxODk4RSIgc3Ryb2tlLXdpZHRoPSIxLjE0Mjg2Ii8+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNOS45OTk5MyA0LjY0MjcyQzEwLjIzNjYgNC42NDI3MiAxMC40Mjg1IDQuODM0NTkgMTAuNDI4NSA1LjA3MTI5TDEwLjQyODUgMTIuNjA4MUwxMy41NTQgOS40ODI1M0MxMy43MjE0IDkuMzE1MTYgMTMuOTkyOCA5LjMxNTE2IDE0LjE2MDEgOS40ODI1M0MxNC4zMjc1IDkuNjQ5OSAxNC4zMjc1IDkuOTIxMjUgMTQuMTYwMSAxMC4wODg2TDEwLjMwMyAxMy45NDU4QzEwLjEzNTYgMTQuMTEzMSA5Ljg2NDI1IDE0LjExMzEgOS42OTY4OCAxMy45NDU4TDUuODM5NzQgMTAuMDg4NkM1LjY3MjM3IDkuOTIxMjUgNS42NzIzNyA5LjY0OTg5IDUuODM5NzQgOS40ODI1M0M2LjAwNzExIDkuMzE1MTYgNi4yNzg0NyA5LjMxNTE2IDYuNDQ1ODMgOS40ODI1M0w5LjU3MTM2IDEyLjYwODFMOS41NzEzNiA1LjA3MTI5QzkuNTcxMzYgNC44MzQ1OSA5Ljc2MzI0IDQuNjQyNzIgOS45OTk5MyA0LjY0MjcyWiIgZmlsbD0iI0YxODk4RSIvPgo8L3N2Zz4K',
    //       iconImageSize: [28, 28],
    //       iconImageOffset: [-14, -14],
    //     },
    //   );
    //
    //   map.geoObjects.add(bottomCircle);
    //   onUpdateBottomAngle?.({
    //     latitude: coords[0],
    //     longitude: coords[1],
    //   });
    //   setBottomAngle(coords);
    //
    //   if (topAngle) {
    //     setGradient(polygon, topAngle, coords);
    //     setDirectionArrow(topAngle, coords);
    //
    //     const angle =
    //       (Math.atan2(coords[1] - topAngle[1], coords[0] - topAngle[0]) * 180) / Math.PI;
    //
    //     bottomCircle.options.set('iconRotate', angle + 180);
    //     if (topCircle) topCircle.options.set('iconRotate', angle);
    //   }
    //
    //   return;
    // }

    if (localMode === 'setInput') {
      const coords = event.get('coords');
      const closest = polygon.geometry?.getClosest(coords);

      polygon.options.set('fill', false);

      if (closest.distance < 250) {
        if (inputMark) map.geoObjects.remove(inputMark);

        const firstPolygonPoint =
          polygon.geometry?.getCoordinates()[0][closest.prevPointIndex];
        const secondPolygonPoint =
          polygon.geometry?.getCoordinates()[0][closest.nextPointIndex];
        const lineAngle =
          (Math.atan2(
            secondPolygonPoint?.[1] - firstPolygonPoint?.[1],
            secondPolygonPoint?.[0] - firstPolygonPoint?.[0],
          ) *
            180) /
          Math.PI;

        inputMark = new ymaps.Placemark(
          closest.position,
          {},
          {
            iconLayout: ymaps.templateLayoutFactory.createClass(
              [
                '<div style="transform:rotate({{options.rotate}}deg);">',
                '{% include "default#image" %}',
                '</div>',
              ].join(''),
            ),
            iconImageHref: CreateWarehouseInputImageBase64,
            iconImageSize: [28, 28],
            iconImageOffset: [0, -28],
            iconRotate: lineAngle + 90,
          },
        );
        map.geoObjects.add(inputMark);

        onUpdateInput?.({
          latitude: closest.position[0],
          longitude: closest.position[1],
        });
      }
    }

    if (localMode === 'setOutput') {
      const coords = event.get('coords');
      const closest = polygon.geometry?.getClosest(coords);

      if (closest.distance < 250) {
        if (outputMark) map.geoObjects.remove(outputMark);

        const firstPolygonPoint =
          polygon.geometry?.getCoordinates()[0][closest.prevPointIndex];
        const secondPolygonPoint =
          polygon.geometry?.getCoordinates()[0][closest.nextPointIndex];
        const lineAngle =
          (Math.atan2(
            firstPolygonPoint[1] - secondPolygonPoint[1],
            firstPolygonPoint[0] - secondPolygonPoint[0],
          ) *
            180) /
          Math.PI;

        outputMark = new ymaps.Placemark(closest.position, {}, {
          iconLayout: ymaps.templateLayoutFactory.createClass(
            [
              '<div style="transform:rotate({{options.rotate}}deg);">',
              '{% include "default#image" %}',
              '</div>',
            ].join(''),
          ),
          iconImageHref: CreateWarehouseOutputImageBase64,
          iconImageSize: [28, 28],
          iconImageOffset: [0, 0],
          iconRotate: lineAngle + 270,
        } as IPlacemarkOptions);
        map.geoObjects.add(outputMark);

        onUpdateOutput?.({
          latitude: closest.position[0],
          longitude: closest.position[1],
        });
      }
    }
  };

  useEffect(() => {
    if (!mapReady) {
      ymaps.ready(['util.calculateArea']).then(() => {
        map = new ymaps.Map('create-warehouse', {
          center: center,
          zoom,
          controls,
          type: 'yandex#satellite' as 'yandex#satellite',
        });

        // const zoomControl = new ymaps.control.ZoomControl({
        //   options: {
        //     position: {
        //       right: 16,
        //       top: 30,
        //     },
        //   },
        // });
        // map.controls.add(zoomControl);

        // ymaps.geolocation.get({
        //   provider: 'auto',
        //   mapStateAutoApply: true,
        // }).then(data => {
        //   console.log(data);
        // });

        setMapReady(true);
        onMapReady?.();
      });
    }

    if (mapReady && map) {
      setLocalMode(mode);

      if (mode === 'setPolygon') {
        if (!polygon) {
          polygon = new ymaps.Polygon([], {}, {
            // Курсор в режиме добавления новых вершин.
            editorDrawingCursor: 'crosshair',
            // Цвет обводки.
            strokeColor: '#FFF1B8',
            // Ширина обводки.
            strokeWidth: 1,
            fill: false,
            strokeStyle: 'solid',
            interactivityModel: 'default#transparent',
          } as IPolygonOptions);

          map.geoObjects.add(polygon);
        }

        // const stateMonitor = new ymaps.Monitor(polygon.editor.state);

        // stateMonitor.add('drawing', function (newValue) {
        //   console.log(newValue);
        // //   polygon.options.set("strokeColor", newValue ? '#FF0000' : '#0000FF');
        // });
        // @ts-ignore
        polygon.editor.startDrawing();

        polygon.geometry?.events.add('change', (event) => {
          // @ts-ignore
          const area = ymaps.util.calculateArea(polygon);
          onSquareChange?.(area);
          onUpdateInput?.(undefined);
          onUpdateOutput?.(undefined);
          inputMark = null;
          outputMark = null;
          markers = [];

          // @ts-ignore
          if (!polygon.geometry?.getBounds()) polygon.editor.startDrawing();

          const newPolygonCoords: number[][] = event.get('newCoordinates')[0];
          if (!newPolygonCoords || newPolygonCoords.length === 0) return;
          // координаты содержит в себе [n1 ... n, n, n1,], где n - координаты вершин
          const correctPolygonCoords = newPolygonCoords.slice(
            0,
            newPolygonCoords.length - 1,
          );

          const intersect =
            correctPolygonCoords.length > 1
              ? checkWarehouseIntersection(correctPolygonCoords, restrictedAreas)
              : false;
          polygon.options.set('strokeColor', intersect ? '#F00' : '#FFF1B8');

          // TODO: вынести в отдельную функцию-утилиту
          // calculate marker
          const bounds: number[][] = polygon.geometry?.getBounds() || [[], []];
          const delta = [
            Math.abs(bounds[0][0] - bounds[1][0]) / 2,
            Math.abs(bounds[0][1] - bounds[1][1]) / 2,
          ];
          const marker = [
            bounds[0][0] > bounds[1][0]
              ? bounds[1][0] + delta[0]
              : bounds[0][0] + delta[0],
            bounds[0][1] > bounds[1][1]
              ? bounds[1][1] + delta[1]
              : bounds[0][1] + delta[1],
          ];

          onSendCoordinates?.(correctPolygonCoords);
          onSetMarker?.(marker);
          onIntersectionCheck?.(intersect);
        });

        polygon.editor.events.add('drawingstop', () => onPolygonEnd?.());
      }

      if (['setPolygon', 'setMarker'].includes(mode)) {
        for (const area of restrictedAreas) {
          restrictedPolygons.push(
            new ymaps.Polygon(
              [area],
              {},
              {
                strokeColor: '#eb5757',
                fill: true,
                fillMethod: 'stretch',
                fillImageHref: `data:image/svg+xml,${encodeURIComponent(`
              <svg width="120" height="240"  xmlns="http://www.w3.org/2000/svg">
                <pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width="4" height="4">
                  <path d="M-1,1 l2,-2
                          M0,4 l4,-4
                          M3,5 l2,-2" 
                        style="stroke:#eb5757;stroke-width:1" />
                </pattern>
                <rect x="0" y="0" width="100%" height="100%" fill="url(#diagonalHatch)"/>
              </svg>
              `)}`,
                strokeWidth: 2,
                strokeStyle: 'solid',
                interactivityModel: 'default#transparent',
              },
            ),
          );
        }

        restrictedPolygons.forEach((p) => map.geoObjects.add(p));
      }

      if (!['setPolygon', 'setMarker'].includes(mode)) {
        // @ts-ignore
        polygon?.editor?.stopDrawing?.();
        polygon?.editor?.stopEditing?.();
        restrictedPolygons.forEach((p) => map.geoObjects.remove(p));
      }

      if (['setInput', 'setOutput', 'setSleeves'].includes(mode)) {
        if (inputMark) map.geoObjects.add(inputMark);
        if (outputMark) map.geoObjects.add(outputMark);
      }
      if (!['setInput', 'setOutput', 'setSleeves'].includes(mode)) {
        map.geoObjects.remove(inputMark as Placemark);
        map.geoObjects.remove(outputMark as Placemark);
      }

      if (!['setTopAngle', 'setBottomAngle'].includes(mode)) {
        map.geoObjects.remove(topCircle);
        map.geoObjects.remove(bottomCircle);
      }

      if (mode === 'setSleeves') {
        markers.forEach((m) => map.geoObjects.add(m));
      }
      if (mode !== 'setSleeves') {
        markers.forEach((m) => map.geoObjects.remove(m));
      }
    }

    return () => {};
  }, [mode]);

  useEffect(() => {
    if (!map || !disposition) return;
    if (slvManager) slvManager.dispose();

    markers.forEach((m) => map.geoObjects.remove(m));
    markers = [];

    slvManager = new SleeveManager(map);
    slvManager.sleeveLength = sleeveLength;

    const sleeves = slvManager.buildSleevesByStartEndPoints(disposition);

    sleeves.forEach((s) => markers.push(s.marker));
    markers.forEach((s) => map.geoObjects.add(s));

    if (onBugsCount) onBugsCount(sleeves.length);
    // TODO: вынести вычисления и создать константы
    // height - 2.74, width - 3.5
    if (onBugsVolume)
      onBugsVolume(sleeves.length * (sleeveLength * 2.74 * 3.5 * Math.PI));
  }, [disposition]);

  useEffect(() => {
    if (map) map.events.add('click', onMapClick);
    return () => {
      if (map) map.events.remove('click', onMapClick);
    };
  });

  useEffect(
    () => () => {
      // в режиме разработке придётся обновлять страницу, чтобы карта не висела
      polygon = null as unknown as Polygon;
      inputMark = null;
      outputMark = null;
      markers = [];
    },
    [],
  );

  return (
    <div>
      {['setTopAngle', 'setBottomAngle'].includes(mode) && (
        <ButtonWithIcon
          icon={CrossIcon}
          className={styles.close}
          onClick={() => onCloseSetAngle?.()}
        >
          закрыть
        </ButtonWithIcon>
      )}

      <div className={styles.container} id="create-warehouse" ref={containerDomRef} />
    </div>
  );
};
CreateMap.defaultProps = {
  center: [DEFAULT_VIEWER_SETTINGS.center[0], DEFAULT_VIEWER_SETTINGS.center[1]],
  zoom: DEFAULT_VIEWER_SETTINGS.zoom,
  controls: DEFAULT_VIEWER_SETTINGS.controls,
};

function setGradient(polygon: ymaps.Polygon, top: number[], bottom: number[]) {
  const coordinates = polygon.geometry!.getCoordinates()[0];
  const { x1, x2, y1, y2 } = getGradientCoords(coordinates, top, bottom);

  polygon.options.set('fill', true);
  polygon.options.set('fillMethod', 'stretch');
  polygon.options.set(
    'fillImageHref',
    `data:image/svg+xml,${encodeURIComponent(`
  <svg
    width="500" height="500" viewBox="0 0 500 500" fill="none" style="transform: rotate(-90deg)" xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      width="500"
      height="500"
      fill="url(#Gradient)"
      fill-opacity="0.6"
    />
    <defs>
    <linearGradient id="Gradient" x1="${x1}" x2="${x2}" y1="${y1}" y2="${y2}">
      <stop offset="0%" stop-color="#007E3C"/>
      <stop offset="100%" stop-color="#F1898E"/>
    </linearGradient>
    </defs>
  </svg>
  `)}`,
  );
}

function setDirectionArrow(top: number[], bot: number[]) {
  if (directionArrow) {
    map.geoObjects.remove(directionArrow);
  }

  const tb = [bot[0] - top[0], bot[1] - top[1]];
  const bt = [top[0] - bot[0], top[1] - bot[1]];
  const t1 = [bot[0] + bt[0] * 0.96, bot[1] + bt[1] * 0.96];
  const b1 = [top[0] + tb[0] * 0.96, top[1] + tb[1] * 0.96];

  // @ts-ignore
  ymaps.modules.require(['geoObject.Arrow'], function (Arrow) {
    directionArrow = new Arrow([t1, b1], null, {
      geodesic: true,
      strokeWidth: 2,
      strokeStyle: 'solid',
      strokeColor: '#66B28A',
    });
    map.geoObjects.add(directionArrow);
  });
}
